<template>
    <product-card-layout
        :thumb="product.mainImage"
        :name="product.name"
        :product-link="productLink"
        :offers-count="product.offers && product.offers.length"
        :is-recommended="product.recommended"
        :_expanded-count="expanded"
        :_expanded-limit="expandedLimit"
        @change-expanded-count="expanded = $event"
        @change-expanded-limit="expandedLimit = $event"
    >
        <template v-slot>
            <div class="d-flex flex-column full-height">
                <div class="font-weight-medium text-truncate text-link">
                    <nuxt-link
                        v-if="$route.name === 'simpleTourSearch'"
                        no-prefetch
                        :title="product.name"
                        :to="productLink"
                        class="text-decoration-none"
                    >
                        {{ product.name }}
                    </nuxt-link>
                    <span v-else :title="product.name">
                        {{ product.name }}
                    </span>
                </div>
                <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" />
                <div class="text-caption mt-2 d-flex flex-column">
                    <div v-if="product.duration" class="d-flex align-center">
                        <v-icon start size="small">mdi-timer-outline</v-icon>
                        <strong class="text-black me-1">
                            {{ `${product.duration} ${$t('day', product.duration)}` }}
                        </strong>
                    </div>
                    <div
                        v-if="product.shortDescription"
                        class="short-description mt-1"
                        v-html="$filters.stripHtml(product.shortDescription)"
                    />
                </div>
            </div>
        </template>
        <template v-slot:priceCol>
            <slot name="price" :offer="minPriceOffer">
                <template v-if="!minPriceOffer.soldOut">
                    <cruise-card-price-block :offer="minPriceOffer" />
                    <v-btn variant="flat" block color="primary" class="mt-2" :to="productLink">
                        {{ $t('book') }}
                    </v-btn>
                </template>
            </slot>
            <div class="text-primary cursor-pointer text-no-wrap mt-2" @click="toggleOffers">
                <span>
                    {{ expanded ? $t('hide') : $t('show') }}
                </span>
                <v-icon color="primary">
                    {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
            </div>
        </template>
        <template v-slot:offers>
            <div v-if="expanded" class="mt-3">
                <v-row>
                    <v-col cols="12" md="4" class="px-6 mb-3">
                        <date-filter-search v-model="dateFilter" outlined :dates-for-filter="datesForFilter" />
                    </v-col>
                </v-row>
                <template v-if="groupOffersBy !== 'none'">
                    <simple-tour-grouped-offers
                        :date-filter="dateFilter"
                        :sorted-filtered-offers="sortedFilteredOffers"
                    >
                        <template v-slot:price="{offer, offerKey}">
                            <slot name="price" :offer="offer" :offer-key="offerKey" />
                        </template>
                    </simple-tour-grouped-offers>
                </template>
                <template v-else>
                    <simple-tour-offer-card
                        v-for="(offer, offerIndex) in sortedFilteredSlicedOffers"
                        :key="offerIndex"
                        :offer="offer"
                        :full-card="false"
                    >
                        <template v-slot:price="{offer}">
                            <slot name="price" :offer="offer" />
                        </template>
                    </simple-tour-offer-card>
                    <v-list-item
                        v-if="expanded === expandedLimit && sortedFilteredOffers.length > expandedLimit"
                        density="compact"
                        class="d-flex justify-center"
                        @click.stop="expand"
                    >
                        <v-icon>mdi-chevron-down</v-icon>
                    </v-list-item>
                </template>
            </div>
        </template>
    </product-card-layout>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import DiscountLabel from '@/components/snippets/DiscountLabel.vue'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import ProductCardLayout from '~/components/search/ProductCardLayout.vue'
    import CruiseCardPriceBlock from '@/components/cruises/search/offers/CruiseCardPriceBlock.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import SimpleTourGroupedOffers from '@/components/simpleTours/search/offers/SimpleTourGroupedOffers.vue'
    import DateFilterSearch from '@/components/snippets/DateFilterSearch.vue'
    import {minPriceOffer} from '~/utils/sort-helpers'
    import SimpleTourOfferCard from '@/components/simpleTours/info/SimpleTourOfferCard.vue'
    import {simpleTourStore} from '~/composables/store-accessor'

    @Component({
        components: {
            SimpleTourOfferCard,
            DateFilterSearch,
            SimpleTourGroupedOffers,
            SupplierNameLabel,
            CruiseCardPriceBlock,
            CommissionAmount,
            DiscountLabel,
            ProductCardLayout,
        },
    })
    export class SimpleTourCard extends Vue {
        @Prop({required: true}) product
        @Prop({default: false, type: Boolean}) isPackage

        expanded = 0
        expandedLimit = 3
        imgError = false
        dateFilter = null

        expand() {
            this.expanded = this.product.offers.length
            this.expandedLimit = this.product.offers.length
        }

        toggleOffers() {
            let expanded = 0
            if (!this.expanded) {
                expanded = this.expandedLimit
            }
            this.expanded = expanded
        }

        get datesForFilter() {
            return this.product.offers.map(o => o.date)
        }

        get groupOffersBy() {
            return simpleTourStore.groupOffersBy
        }

        get sortedFilteredOffers() {
            let offers = [...this.product.offers]

            if (this.dateFilter) {
                offers = offers.filter(offer => offer.date === this.dateFilter)
            }
            return offers
        }

        get sortedFilteredSlicedOffers() {
            return this.sortedFilteredOffers.slice(0, this.expanded)
        }

        get minPriceOffer() {
            return minPriceOffer(this.product)
        }

        get productLink() {
            return simpleTourRuntimeStore.infoPageLink(this.product, simpleTourStore.searchRequest)
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default SimpleTourCard
</script>

<style scoped lang="scss">
    .short-description {
        white-space: normal;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
